import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE } from "../../../config";
import { supabase } from "../../../supaBaseClient";
import Footer from "../../homepage/footer";

const ExamTracking = () => {
  const [examAttempts, setExamAttempts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [userId, setUserId] = useState(null);

  const [newAttempt, setNewAttempt] = useState({
    year: new Date().getFullYear().toString(),
    month: "May",
    paper_number: "1",
    score: "",
    total: "",
    grade: "",
    attempt_date: "",
  });

  // Supabase auth listener
  useEffect(() => {
    let unsub = () => {};

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) setUserId(data.user.id);
      else setUserId(null);

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        const user = session?.user;
        setUserId(user ? user.id : null);
      });

      unsub = () => subscription.unsubscribe();
    })();

    return () => unsub();
  }, []);

  // Fetch exam attempts when logged in
  useEffect(() => {
    if (userId) fetchExamAttempts();
  }, [userId]);

  const fetchExamAttempts = async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API_BASE}/api/exam-tracking/${userId}`);
      setExamAttempts(
        res.data.sort(
          (a, b) =>
            new Date(b.attempt_date).getTime() -
            new Date(a.attempt_date).getTime()
        )
      );
    } catch (err) {
      console.error("Error fetching exam attempts:", err);
    }
  };

  const handleAddAttempt = async () => {
    if (!userId) {
      toast.error("User not logged in.");
      return;
    }

    const { year, month, paper_number, score, total, grade, attempt_date } =
      newAttempt;

    if (!score || !total || !grade || !attempt_date) {
      toast.error("Please fill in all fields.");
      return;
    }

    if (!["May", "June", "November"].includes(month)) {
      toast.error("Invalid month selected.");
      return;
    }

    const examPaperTitle = `${year} ${month} - Paper ${paper_number}`;

    const attemptData = {
      user_uid: userId,
      exam_paper: examPaperTitle,
      score: parseInt(score, 10),
      total: parseInt(total, 10),
      grade,
      attempt_date,
    };

    try {
      await axios.post(`${API_BASE}/api/exam-tracking`, attemptData);
      toast.success("Exam attempt logged successfully.");
      setNewAttempt({
        year: new Date().getFullYear().toString(),
        month: "May",
        paper_number: "1",
        score: "",
        total: "",
        grade: "",
        attempt_date: "",
      });
      setShowForm(false);
      fetchExamAttempts();
    } catch (err) {
      console.error("Error saving exam attempt:", err);
    }
  };

  const handleDeleteAttempt = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;

    try {
      await axios.delete(`${API_BASE}/api/exam-tracking/${id}`);
      fetchExamAttempts();
    } catch (err) {
      console.error("Error deleting exam attempt:", err);
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    if (examAttempts.length === 0) return { avgScore: 0, avgPercentage: 0, totalAttempts: 0, highestGrade: 'N/A' };

    const totalScore = examAttempts.reduce((sum, attempt) => sum + attempt.score, 0);
    const totalPossible = examAttempts.reduce((sum, attempt) => sum + attempt.total, 0);
    const avgScore = (totalScore / examAttempts.length).toFixed(1);
    const avgPercentage = ((totalScore / totalPossible) * 100).toFixed(1);
    
    const grades = examAttempts.map(a => parseInt(a.grade)).filter(g => !isNaN(g));
    const highestGrade = grades.length > 0 ? Math.max(...grades) : 'N/A';

    return {
      avgScore,
      avgPercentage,
      totalAttempts: examAttempts.length,
      highestGrade
    };
  };

  const stats = calculateStats();

  // Grade color helper
  const getGradeColor = (grade) => {
    const numGrade = parseInt(grade);
    if (numGrade >= 7) return 'bg-green-100 text-green-700 border-green-300';
    if (numGrade >= 5) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (numGrade >= 4) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-orange-100 text-orange-700 border-orange-300';
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>Exam Paper Tracker | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/examtracking" />
        <meta name="description" content="Track your exam paper attempts, monitor your progress, and improve your GCSE Maths exam performance." />
      </Helmet>

      <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl flex-grow">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Exam Paper Tracker</h1>
          <p className="text-lg text-gray-600">Monitor your progress and track your exam performance</p>
        </div>

        {/* Statistics Cards */}
        {examAttempts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-teal-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Total Attempts</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.totalAttempts}</p>
                </div>
                <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Average Score</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.avgScore}</p>
                </div>
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Average %</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.avgPercentage}%</p>
                </div>
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Highest Grade</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.highestGrade}</p>
                </div>
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add New Attempt Button */}
        <div className="mb-8">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {showForm ? 'Cancel' : 'Log New Attempt'}
          </button>
        </div>

        {/* Add Attempt Form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-t-4 border-teal-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Log a New Exam Attempt</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Year */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Year</label>
                <select
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.year}
                  onChange={(e) => setNewAttempt({ ...newAttempt, year: e.target.value })}
                >
                  {Array.from({ length: new Date().getFullYear() - 2016 }, (_, i) => (
                    <option key={i} value={2017 + i}>{2017 + i}</option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Month</label>
                <select
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.month}
                  onChange={(e) => setNewAttempt({ ...newAttempt, month: e.target.value })}
                >
                  {["May", "June", "November"].map((month) => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              {/* Paper */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Paper Number</label>
                <select
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.paper_number}
                  onChange={(e) => setNewAttempt({ ...newAttempt, paper_number: e.target.value })}
                >
                  {["1", "2", "3", "4", "5", "6"].map((paper) => (
                    <option key={paper} value={paper}>{`Paper ${paper}`}</option>
                  ))}
                </select>
              </div>

              {/* Score */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Score</label>
                <input
                  type="number"
                  placeholder="e.g. 75"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.score}
                  onChange={(e) => setNewAttempt({ ...newAttempt, score: e.target.value })}
                />
              </div>

              {/* Total Marks */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Total Marks</label>
                <input
                  type="number"
                  placeholder="e.g. 100"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.total}
                  onChange={(e) => setNewAttempt({ ...newAttempt, total: e.target.value })}
                />
              </div>

              {/* Grade */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Grade</label>
                <input
                  type="text"
                  placeholder="e.g. 7"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.grade}
                  onChange={(e) => setNewAttempt({ ...newAttempt, grade: e.target.value })}
                />
              </div>

              {/* Date */}
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Attempt Date</label>
                <input
                  type="date"
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                  value={newAttempt.attempt_date}
                  onChange={(e) => setNewAttempt({ ...newAttempt, attempt_date: e.target.value })}
                />
              </div>
            </div>

            <button
              onClick={handleAddAttempt}
              className="mt-6 px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold shadow-lg"
            >
              Save Attempt
            </button>
          </div>
        )}

        {/* Past Attempts */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800">Past Exam Attempts</h2>
            <p className="text-gray-600 mt-1">Review your exam history and track improvements</p>
          </div>

          {examAttempts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">No exam attempts logged yet</p>
              <p className="text-gray-500 text-sm mt-2">Click "Log New Attempt" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="hidden md:block">
                {/* Desktop: Cards View */}
                <div className="p-6 space-y-4">
                  {examAttempts.map((attempt) => {
                    const percentage = ((attempt.score / attempt.total) * 100).toFixed(1);
                    return (
                      <div key={attempt.id} className="border-2 border-gray-100 rounded-xl p-6 hover:border-teal-200 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-800 mb-2">{attempt.exam_paper}</h3>
                            <div className="flex items-center gap-6 text-sm">
                              <span className="text-gray-600">
                                <span className="font-semibold text-gray-800">{attempt.score}</span> / {attempt.total} marks
                              </span>
                              <span className="text-gray-600">
                                {percentage}%
                              </span>
                              <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${getGradeColor(attempt.grade)}`}>
                                Grade {attempt.grade}
                              </span>
                              <span className="text-gray-500">
                                {new Date(attempt.attempt_date).toLocaleDateString("en-GB")}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteAttempt(attempt.id)}
                            className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mobile: Table View */}
              <div className="md:hidden overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-teal-500 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Exam Paper</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Score</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Grade</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Date</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {examAttempts.map((attempt) => (
                      <tr key={attempt.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{attempt.exam_paper}</td>
                        <td className="px-4 py-3 text-center text-sm">{attempt.score}/{attempt.total}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getGradeColor(attempt.grade)}`}>
                            {attempt.grade}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {new Date(attempt.attempt_date).toLocaleDateString("en-GB")}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleDeleteAttempt(attempt.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ExamTracking;